import React, {
  useMemo,
  useState,
} from 'react';
import Modal from '@mui/material/Modal';
import {
  Box,
  Button,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { DateRangePicker } from 'rsuite';
import { DateRange } from 'rsuite/esm/DateRangePicker';
import dayjs from 'dayjs';
import { RootState } from '../../../redux/rootState';
import { Cancel } from '../../../media/images/Cancel';
import { PrintIcon } from '../../../assets/TableIcons';
import DataTable from '../../../Common/DataTable';
import { Datatablestyle } from '../../ComponentNavBar/utils/Constant';
import { ForecastReportTableData } from '../../ComponentNavBar/utils/TypeDashboard';
import { predefinedRanges } from '../../../Common/SearchHeader/utils/DatePickerRange';
import { showErrorToaster } from '../../../Common/ComponentToast/ComponentSuccessToasts';
import { getForecastReport } from '../UnscheduledJobs/addJob/redux/jobScheduleSlice';
import { exportToExcelData } from '../../../Common/PageHeader/helper';
import { stripCustPrefix } from '../../../utils/CommonFunctions';

interface IForecastReportModal {
  open: boolean;
  handleClose: () => void;
}

export default function ForecastReportModal({
  open, handleClose,
}: IForecastReportModal) {
  const dispatch = useDispatch();
  const [dateOpen, setDateOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const handleDateChange = (value: DateRange | null) => {
    setDateRange(value);
    setEnabled(true);
  };

  const getData = () => {
    if (enabled) {
      if (dateRange) {
        const FromDate = dayjs(dateRange?.[0])?.format('DD-MM-YYYY');
        const ToDate = dayjs(dateRange?.[1])?.format('DD-MM-YYYY');
        const payload = {
          FromDate,
          ToDate,
        };
        dispatch(getForecastReport(payload));
      } else {
        showErrorToaster('Please select valid date range to fetch report');
      }
    }
  };
  const { forecastReportData } = useSelector((state: RootState) => state.jobScheduleReducer);

  const receivedReports = useMemo(() => {
    if (forecastReportData) {
      setEnabled(false);
    }
    const received = forecastReportData?.map((item, index) => ({
      id: String(index + 1),
      jobId: item?.JobId,
      bidId: item?.BidId,
      scheduleInfoToolTip: item?.UnitIds,
      customerId: stripCustPrefix(item?.CustId),
      customer: item?.CustomerName,
      site: item?.SiteName,
      siteState: item?.SiteState,
      nosortBidAmount: `$ ${item?.BidAmount}`,
    }));
    return received;
  }, [forecastReportData]);

  const receivedColumns = [
    { key: 'jobId', label: 'Job #' },
    { key: 'bidId', label: 'Bid #' },
    { key: 'scheduleInfoToolTip', label: 'Schedule Info' },
    { key: 'customerId', label: 'Customer Id' },
    { key: 'customer', label: 'Customer' },
    { key: 'site', label: 'Job Site' },
    { key: 'siteState', label: 'Job Site State' },
    { key: 'nosortBidAmount', label: 'Bid Amount' },
  ];

  const downloadExcel = () => {
    if (forecastReportData?.length > 0) {
      const exportedData = forecastReportData?.map((items) => ({
        JobId: items?.JobId,
        BidId: items?.BidId,
        'Schedule Info': items?.UnitIds?.join(', '),
        CustomerId: stripCustPrefix(items?.CustId),
        Customer: items?.CustomerName,
        Site: items?.SiteName,
        'Site State': items?.SiteState,
        'Bid Amount ($)': Number(items?.BidAmount),
      }));
      exportToExcelData(
        {
          data: exportedData?.length > 0 ? exportedData : [],
          removeSn: true,
        },
        'FORECAST REPORT',
      );
    } else {
      showErrorToaster('No records to export');
    }
  };

  const bidAmountSum = useMemo(() => {
    if (forecastReportData?.length > 0) {
      const total = forecastReportData.reduce((sum, x) => sum + (Number(x.BidAmount) || 0), 0);
      return total;
    } return 0;
  }, [forecastReportData]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={Datatablestyle}>
        <div className="customerInfo_container flex-row">
          <div className="customerInfo">
            Forecast Report
          </div>
          <div className="cancel-icon" onClick={() => handleClose()}>
            <Cancel />
          </div>

        </div>
        <div className="customModalBtnContainer">
          <div className="forecastButtonContainer">
            <div className="dateRangeFilterRangeDatePicker">
              <p className="analystModalLabel">Select Date Range</p>
              <DateRangePicker
                name="Range"
                open={dateOpen}
                onClick={() => setDateOpen(!dateOpen)}
                onOk={() => setDateOpen(false)}
                value={dateRange}
                onChange={handleDateChange}
                ranges={predefinedRanges}
                placeholder="Date Range"
                className="dateRangeFilterCustomDateRangeCalender"
                character=" - "
                showCleanButton
                format="MM-dd-yyyy"
                placement="bottomStart"
              />
            </div>
            <Button
              className={enabled ? 'button_save_and_next' : 'button_cancel cursorNotAllowed'}
              sx={{ height: 36, marginTop: '20px' }}
              onClick={getData}
            >
              Get Forecast Report
            </Button>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px',
          }}
          >
            <div className="pointCursor" onClick={downloadExcel}>
              <PrintIcon />
            </div>
            {receivedReports?.length > 0 && (
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                Total Forecast Amount: $
                {' '}
                {bidAmountSum}
              </div>
            )}
          </div>
        </div>
        <DataTable<ForecastReportTableData>
          data={receivedReports || []}
          columns={receivedColumns}
          handleData={() => {}}
          totalRecords={10}
        />
      </Box>
    </Modal>
  );
}
